/**
 * DriversTableRow - Linha da tabela de motoristas
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Checkbox,
} from '@mui/material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { formatDate } from '../../common/util/formatter';
import CollectionActions from './CollectionActions';

// Helper para verificar se está expirado
const isExpired = (expirationTime) => {
  if (!expirationTime) return false;
  return new Date(expirationTime) < new Date();
};

// Helper para cores do status - USA TOKENS DINÂMICOS
const getStatusColors = (status, tokens) => {
  const colors = {
    active: {
      text: tokens?.semantic?.success,
      bg: tokens?.semantic?.successBg,
      border: `${tokens?.semantic?.success}40`,
    },
    disabled: {
      text: tokens?.semantic?.error,
      bg: tokens?.semantic?.errorBg,
      border: `${tokens?.semantic?.error}40`,
    },
    expired: {
      text: tokens?.semantic?.warning,
      bg: tokens?.semantic?.warningBg,
      border: `${tokens?.semantic?.warning}40`,
    },
  };

  return colors[status] || colors.active;
};

// Helper para determinar status do motorista
const getDriverStatus = (driver, t) => {
  if (driver.disabled) {
    return { key: 'disabled', label: t('sharedDisabled') };
  }
  if (isExpired(driver.expirationTime)) {
    return { key: 'expired', label: t('userExpired') };
  }
  return { key: 'active', label: t('sharedActive') };
};

const DriversTableRow = memo(({
  row,
  onRefresh,
  onNavigate,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const status = getDriverStatus(row, t);
  const statusColors = getStatusColors(status.key, tokens);

  // Cor do avatar baseada no status
  const avatarBgColor = statusColors.text;

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 0.5, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 0.5, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarBgColor}20`,
            color: avatarBgColor,
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
        >
          {row.name?.charAt(0)?.toUpperCase() || 'D'}
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="caption" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
          {row.name}
        </Typography>
      </TableCell>

      {/* UniqueId */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: tokens?.text?.secondary }}>
          {row.uniqueId || '-'}
        </Typography>
      </TableCell>

      {/* Phone */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.attributes?.form?.phone || row.phone || '-'}
        </Typography>
      </TableCell>

      {/* License */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.attributes?.form?.document || row.attributes?.driverLicense || '-'}
        </Typography>
      </TableCell>

      {/* Expiration */}
      <TableCell sx={{ py: 0.5 }}>
        <Tooltip title={row.expirationTime ? new Date(row.expirationTime).toLocaleString() : ''}>
          <Typography
            variant="body2"
            className={isExpired(row.expirationTime) ? 'time-color-error' : ''}
            sx={{
              fontWeight: isExpired(row.expirationTime) ? 600 : 400,
              fontSize: '0.8rem',
            }}
          >
            {row.expirationTime ? formatDate(row.expirationTime) : '-'}
          </Typography>
        </Tooltip>
      </TableCell>

      {/* Status */}
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Chip
          label={status.label}
          size="small"
          className={`status-${status.key}`}
          sx={{
            minWidth: 70,
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            border: `1px solid ${statusColors.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 0.5 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/driver"
          endpoint="drivers"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

DriversTableRow.displayName = 'DriversTableRow';

export default DriversTableRow;
